import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';

dayjs.extend(customParseFormat);

const timeOptions = (() => {
  const newTimeOptions = [];
  const format = 'HH:mm';

  for (let i = 0; i < 24; i++) {
    for (let j = 0; j < 60; j += 30) {
      const time = dayjs().hour(i).minute(j);
      newTimeOptions.push({
        label: time.format(format),
        value: time.format('HH:mm'),
      });
    }
  }

  return newTimeOptions;
})();
const Input = (props) => <components.Input {...props} isHidden={false} />;

export default function StylishTimePicker({
  value,
  disableDateTimeLabel,
  timeLabel,
  ...props
}) {
  const [time, setTime] = useState();
  const [localTimeOptions, setLocalTimeOptions] = useState([]);
  const user = useSelector((state) => state.prepare.user);
  const selectRef = useRef();
  const [inputValue, setInputValue] = useState('00:00');

  useEffect(() => {
    if (user.timeFormat === 'hour12Mode') {
      setLocalTimeOptions(
        timeOptions.map((option) => ({
          ...option,
          label: dayjs(option.value, 'HH:mm').format('h:mm A'),
        }))
      );
    } else {
      setLocalTimeOptions(timeOptions);
    }
  }, [user.timeFormat]);

  useEffect(() => {
    if (value) {
      setTime(value);
      setInputValue(value);
      if (typeof props.onBlur === 'function') {
        props.onBlur();
      }
    }
  }, [value, props]);

  const formatOptionLabel = ({ label }, context) => {
    if (context.context === 'menu') {
      return (
        <div>
          <span className="material-symbols-outlined icon">
            nest_clock_farsight_analog
          </span>
          <div className="label">{label}</div>
        </div>
      );
    } else {
      return <div className="label">{label}</div>;
    }
  };

  const checkIfValid = (inputValue) => {
    return user.timeFormat === 'hour12Mode'
      ? dayjs(inputValue, 'h:mm A', true).isValid() ||
          dayjs(inputValue, 'h:mmA', true).isValid() ||
          dayjs(inputValue, 'hmmA', true).isValid() ||
          dayjs(inputValue, 'hmm A', true).isValid()
      : dayjs(inputValue, 'HH:mm', true).isValid() ||
          dayjs(inputValue, 'HHmm', true).isValid();
  };

  return (
    <>
      <div className="col-md-6 mt-2 mt-md-0">
        {!disableDateTimeLabel && (
          <label className="form-label">
            {timeLabel ? `${timeLabel}:` : 'Time:'}
          </label>
        )}
        <div className={'form-selectbox time-select'}>
          <CreatableSelect
            ref={selectRef}
            onInputChange={(inputValue, { action }) => {
              if (action === 'input-change') {
                setInputValue(inputValue);
              }
            }}
            inputValue={inputValue}
            components={{
              Input,
            }}
            className="react-select-container"
            classNamePrefix="react-select"
            options={localTimeOptions}
            openMenuOnClick={false}
            onBlur={() => {
              if (inputValue?.length === 0) {
                if (user.timeFormat === 'hour12Mode') {
                  setInputValue('12:00 AM');
                  setTime('12:00 AM');
                } else {
                  setInputValue('00:00');
                  setTime('00:00');
                }
              } else if (checkIfValid(inputValue)) {
                const time24 =
                  user.timeFormat === 'hour12Mode'
                    ? dayjs(inputValue, 'HH:mm A').format('h:mm A')
                    : dayjs(inputValue, 'HH:mm').format('HH:mm');
                setTime(time24);
                setInputValue(time24);
              } else {
                //not valid, return to original
                setInputValue(time);
              }
            }}
            value={
              inputValue?.length < 1
                ? null
                : {
                    value: time,
                    label:
                      user.timeFormat === 'hour12Mode'
                        ? dayjs(time, 'h:mm A').format('h:mm A')
                        : dayjs(time, 'HH:mm').format('HH:mm'),
                  }
            }
            onKeyDown={(e) => {
              //limit the input
              if (user.timeFormat === 'hour12Mode') {
                if (
                  !/^[\d:AMPamp\s]+$/.test(e.key) &&
                  e.key !== 'Backspace' &&
                  e.key !== 'ArrowLeft' &&
                  e.key !== 'ArrowRight'
                ) {
                  e.preventDefault();
                }
              } else {
                if (
                  !/^[\d:]+$/.test(e.key) &&
                  e.key !== 'Backspace' &&
                  e.key !== 'ArrowLeft' &&
                  e.key !== 'ArrowRight'
                ) {
                  e.preventDefault();
                }
              }
              if (e.key === 'Enter') {
                if (inputValue?.length === 0) {
                  if (user.timeFormat === 'hour12Mode') {
                    setInputValue('12:00 AM');
                    setTime('12:00 AM');
                  } else {
                    setInputValue('00:00');
                    setTime('00:00');
                  }
                  selectRef.current.blur();
                } else if (checkIfValid(inputValue)) {
                  const time24 =
                    user.timeFormat === 'hour12Mode'
                      ? dayjs(inputValue, 'HH:mm A').format('h:mm A')
                      : dayjs(inputValue, 'HH:mm').format('HH:mm');
                  setTime(time24);
                  setInputValue(time24);
                  selectRef.current.blur();
                } else {
                  //not valid, return to original
                  setInputValue(time);
                  selectRef.current.blur();
                }
              }
            }}
            onChange={(selected) => {
              const time24 =
                user.timeFormat === 'hour12Mode'
                  ? dayjs(selected.value, 'HH:mm').format('h:mm A')
                  : dayjs(selected.value, 'HH:mm').format('HH:mm');
              setInputValue(time24);
              setTime(time24);
            }}
            placeholder={''}
            filterOption={false}
            menuPlacement="auto"
            noOptionsMessage={() => 'No Items Found'}
            formatOptionLabel={formatOptionLabel}
            isValidNewOption={(inputValue) => {
              return checkIfValid(inputValue);
            }}
          />
        </div>
      </div>
    </>
  );
}
